import { useCallback, useEffect, useState, type JSX } from 'react';
import { SaveRow } from '../../components/SaveRow.tsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { optionalNumber } from '../../lib/numbers.ts';
import { api, type CourseTree } from '../../api.ts';

/**
 * The shape of a course: modules, and the lessons inside them.
 *
 * Order is edited with move buttons rather than drag and drop — same reasoning as the ordering
 * question, plus one more: this list is edited rarely and read often, so seeing at a glance what
 * position something holds beats being able to fling it around.
 *
 * Deleting is guarded on the server: a module or lesson students have already handed work into
 * refuses to go, and says why. The button stays enabled so the teacher gets that explanation
 * instead of a mysteriously dead control.
 */
/**
 * A module's name, edited and saved deliberately.
 *
 * Its own component because each row needs its own draft and its own confirmation: one shared
 * "saved" flag across ten modules would light up next to whichever row the teacher was looking
 * at, which is worse than no confirmation at all.
 */
function ModuleTitle({
  moduleId,
  title,
  onSaved,
}: {
  moduleId: string;
  title: string;
  onSaved: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState(title);

  // A rename by somebody else, or a reload, must not be overwritten by a stale draft.
  useEffect(() => setDraft(title), [title]);

  return (
    <>
      <input
        className="input input--inline"
        value={draft}
        aria-label="Название модуля"
        onChange={(e) => setDraft(e.target.value)}
      />
      <SaveRow
        label="Сохранить"
        disabled={draft.trim() === title || draft.trim() === ''}
        onSave={async () => {
          await api.authoring.updateModule(moduleId, { title: draft.trim() });
          onSaved();
        }}
      />
    </>
  );
}

export function CourseEditorPage(): JSX.Element {
  const { courseId } = useParams();
  const [tree, setTree] = useState<CourseTree | null>(null);
  // Deleting the course leaves nothing to look at, so the page has to go somewhere afterwards.
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newModule, setNewModule] = useState('');
  // The certificate lifetime as typed, so the button can tell whether anything actually changed.
  const [months, setMonths] = useState('');
  const [newLesson, setNewLesson] = useState<Record<string, string>>({});

  const load = useCallback((): void => {
    if (!courseId) return;
    api.authoring
      .courseTree(courseId)
      .then(setTree)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить курс'),
      );
  }, [courseId]);

  useEffect(load, [load]);

  const savedMonths =
    tree?.course.certificateValidMonths === null || tree?.course.certificateValidMonths === undefined
      ? ''
      : String(tree.course.certificateValidMonths);

  // Reseed whenever the server's answer changes, so the draft never argues with what was saved.
  useEffect(() => setMonths(savedMonths), [savedMonths]);

  /** Every mutation funnels through here, so one failure message and one reload cover them all. */
  async function run(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await action();
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  }

  if (error && !tree) {
    return (
      <div className="stack" style={{ paddingTop: 20 }}>
        <div className="notice notice--accent">{error}</div>
        <Link to="/admin" className="back">
          ← Курсы
        </Link>
      </div>
    );
  }

  if (!tree) return <p className="muted">Загружаем…</p>;

  const { course, modules } = tree;
  const lessonCount = modules.reduce((sum, m) => sum + m.lessons.length, 0);

  function reordered(ids: string[], index: number, delta: number): string[] | null {
    const target = index + delta;
    if (target < 0 || target >= ids.length) return null;
    const order = [...ids];
    const [moved] = order.splice(index, 1);
    if (moved === undefined) return null;
    order.splice(target, 0, moved);
    return order;
  }

  return (
    <>
      <div className="page-head">
        <Link to="/admin" className="back">
          ← Курсы
        </Link>
        <h1>{course.title}</h1>
        <div className="row">
          <span className={`badge${course.isPublished ? ' badge--live' : ''}`}>
            {course.isPublished ? 'опубликован' : 'черновик'}
          </span>
          <span className="muted">
            /{course.slug} · модулей: {modules.length} · уроков: {lessonCount}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            disabled={busy}
            onClick={() => void run(() => api.authoring.publish(course.id, !course.isPublished))}
          >
            {course.isPublished ? 'Снять с публикации' : 'Опубликовать'}
          </button>
        </div>
        {!course.isPublished ? (
          <p className="muted" style={{ fontSize: '0.86rem' }}>
            Пока курс не опубликован, зачисленные ученики его не видят.
          </p>
        ) : null}
      </div>

      {error ? <p className="error">{error}</p> : null}

      <section className="editcard stack">
        <strong>Сертификат</strong>
        <label className="checkline">
          <input
            type="checkbox"
            checked={course.issuesCertificate}
            disabled={busy}
            onChange={(e) =>
              void run(() =>
                api.authoring.updateCourse(course.id, { issues_certificate: e.target.checked }),
              )
            }
          />
          <span>Выдавать сертификат по завершении курса</span>
        </label>

        {course.issuesCertificate ? (
          <div className="row">
            <div className="field" style={{ maxWidth: 220, marginBottom: 0 }}>
              <label htmlFor="cert-months">Действителен, месяцев</label>
              <input
                id="cert-months"
                className="input"
                type="number"
                min={1}
                max={600}
                value={months}
                placeholder="бессрочно"
                disabled={busy}
                onChange={(e) => setMonths(e.target.value)}
              />
            </div>

            {/*
             * Saved on a button, not on losing focus. The field used to write to the server the
             * moment the cursor left it, which is silent in both directions: no confirmation that
             * it worked, and no warning when it did not.
             */}
            <SaveRow
              disabled={months === savedMonths}
              label="Сохранить срок"
              onSave={async () => {
                const raw = months.trim();
                await api.authoring.updateCourse(course.id, {
                  certificate_valid_months: optionalNumber(raw),
                });
                load();
              }}
            />
            <span className="muted" style={{ fontSize: '0.84rem' }}>
              Пусто — сертификат не истекает. Для корпоративного обучения обычно 12.
            </span>
          </div>
        ) : null}

        <p className="muted" style={{ fontSize: '0.84rem' }}>
          Сертификат выдаётся сам, когда ученик закрывает последний урок курса, и получает номер для
          публичной проверки.
        </p>
      </section>

      {modules.map((courseModule, moduleIndex) => (
        <section key={courseModule.id} className="editcard">
          <header className="editcard__head">
            <span className="module__index">{String(moduleIndex + 1).padStart(2, '0')}</span>
            <ModuleTitle
              moduleId={courseModule.id}
              title={courseModule.title}
              onSaved={load}
            />
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={busy || moduleIndex === 0}
              aria-label="Модуль выше"
              onClick={() => {
                const order = reordered(
                  modules.map((m) => m.id),
                  moduleIndex,
                  -1,
                );
                if (order) void run(() => api.authoring.reorder('module', course.id, order));
              }}
            >
              ↑
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={busy || moduleIndex === modules.length - 1}
              aria-label="Модуль ниже"
              onClick={() => {
                const order = reordered(
                  modules.map((m) => m.id),
                  moduleIndex,
                  1,
                );
                if (order) void run(() => api.authoring.reorder('module', course.id, order));
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small btn--danger"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Удалить модуль «${courseModule.title}» со всеми уроками?`)) {
                  void run(() => api.authoring.deleteModule(courseModule.id));
                }
              }}
            >
              Удалить
            </button>
          </header>

          {courseModule.lessons.length === 0 ? (
            <p className="muted" style={{ padding: '4px 0 10px' }}>
              В модуле пока нет уроков.
            </p>
          ) : null}

          {courseModule.lessons.map((lesson, lessonIndex) => (
            <div key={lesson.id} className="editrow">
              <Link to={`/admin/lessons/${lesson.id}`} className="editrow__main">
                {lesson.title}
              </Link>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={busy || lessonIndex === 0}
                aria-label="Урок выше"
                onClick={() => {
                  const order = reordered(
                    courseModule.lessons.map((l) => l.id),
                    lessonIndex,
                    -1,
                  );
                  if (order)
                    void run(() => api.authoring.reorder('lesson', courseModule.id, order));
                }}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={busy || lessonIndex === courseModule.lessons.length - 1}
                aria-label="Урок ниже"
                onClick={() => {
                  const order = reordered(
                    courseModule.lessons.map((l) => l.id),
                    lessonIndex,
                    1,
                  );
                  if (order)
                    void run(() => api.authoring.reorder('lesson', courseModule.id, order));
                }}
              >
                ↓
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--small btn--danger"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`Удалить урок «${lesson.title}»?`)) {
                    void run(() => api.authoring.deleteLesson(lesson.id));
                  }
                }}
              >
                Удалить
              </button>
            </div>
          ))}

          <div className="row" style={{ marginTop: 10 }}>
            <input
              className="input input--inline"
              placeholder="Название нового урока"
              value={newLesson[courseModule.id] ?? ''}
              onChange={(e) => setNewLesson({ ...newLesson, [courseModule.id]: e.target.value })}
            />
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={busy || !(newLesson[courseModule.id] ?? '').trim()}
              onClick={() => {
                const title = (newLesson[courseModule.id] ?? '').trim();
                void run(async () => {
                  await api.authoring.createLesson(courseModule.id, title);
                  setNewLesson({ ...newLesson, [courseModule.id]: '' });
                });
              }}
            >
              + Урок
            </button>
          </div>
        </section>
      ))}

      <div className="row" style={{ marginTop: 20 }}>
        <input
          className="input input--inline"
          placeholder="Название нового модуля"
          value={newModule}
          onChange={(e) => setNewModule(e.target.value)}
        />
        <button
          type="button"
          className="btn"
          disabled={busy || newModule.trim().length === 0}
          onClick={() =>
            void run(async () => {
              await api.authoring.createModule(course.id, newModule.trim());
              setNewModule('');
            })
          }
        >
          + Модуль
        </button>
      </div>

      {/*
       * Deleting the course itself, at the very bottom and set apart.
       *
       * Last because it undoes everything above it, and a control that removes a course should not
       * sit within reach of the one that adds a module. The server refuses while certificates or
       * handed-in work exist, so the worst this button can do is report why it cannot.
       */}
      <div className="lessonend">
        <p className="muted lessonend__note">
          Удалить курс можно, пока по нему не выдан ни один сертификат и никто не сдавал работ.
        </p>
        <button
          type="button"
          className="btn btn--ghost btn--danger"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Удалить курс «${course.title}» со всеми модулями и уроками?`)) {
              return;
            }

            void (async () => {
              setBusy(true);
              setError('');

              try {
                await api.authoring.deleteCourse(course.id);
                void navigate('/admin');
              } catch (cause) {
                setError(cause instanceof Error ? cause.message : 'Не удалось удалить курс');
                setBusy(false);
              }
            })();
          }}
        >
          Удалить курс
        </button>
      </div>
    </>
  );
}
